import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { LocationFourIcon, OptionIcon, TravelTwoIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import LineTab from '@repo/ui/tabs'
import { isAfter } from 'date-fns'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { OtoIndividualTransferMethodEnum } from '../../../../lib/types/enums/enrollment/oto-individual-transfer-method.enum'
import { OtoIndividualPropositionInput } from '../../../../lib/types/models/enrollment-proposition/oto-individual-proposition-input'
import {
  getOtoIndividualRowId,
  OtoIndividualRow,
  OtoIndividualRowDraft
} from '../utils/oto-individual-utils'
import OtoIndividualProductListSelection from './oto-individual-product-list-selection'
import OtoIndividualPropositionDetails, {
  OtoIndividualDetailsValues
} from './oto-individual-proposition-details'

type Props = {
  isOpen: boolean
  defaultIndex?: number
  defaultValues?: OtoIndividualPropositionInput
  // drives the age limits of the région de séjour and of its lieu d'arrivée
  childBirthDate?: string
  onClose: () => void
  handleSubmit: (values: OtoIndividualPropositionInput) => void
}

const OtoIndividualPropositionFormModal = ({
  isOpen,
  defaultIndex,
  defaultValues,
  childBirthDate,
  onClose,
  handleSubmit
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [selectedRow, setSelectedRow] = useState<OtoIndividualRow | undefined>()
  const [drafts, setDrafts] = useState<Record<string, OtoIndividualRowDraft>>({})
  const [details, setDetails] = useState<OtoIndividualDetailsValues>({
    withOption: false,
    transferMethod: undefined
  })

  // re-seed the modal state from the default values every time it opens
  useEffect(() => {
    if (!isOpen) return
    setActiveIndex(defaultIndex ?? 0)
    if (!defaultValues) {
      setSelectedRow(undefined)
      setDrafts({})
      setDetails({
        withOption: true,
        transferMethod: OtoIndividualTransferMethodEnum.AIRPORT_PICKUP
      })
      return
    }
    const rowId = getOtoIndividualRowId(defaultValues.product, defaultValues.numberOfWeeks)
    setSelectedRow({
      rowId,
      product: defaultValues.product,
      numberOfWeeks: defaultValues.numberOfWeeks
    })
    setDrafts({
      [rowId]: {
        departureDate: defaultValues.departureDate,
        returnDate: defaultValues.returnDate,
        numberOfHours: defaultValues.numberOfHours,
        regionOfStayId: defaultValues.regionOfStay.id,
        arrivalPlaceId: defaultValues.arrivalPlace.id
      }
    })
    setDetails({
      withOption: defaultValues.withOption,
      transferMethod: defaultValues.transferMethod
    })
  }, [isOpen, defaultValues, defaultIndex])

  const selectedDraft = selectedRow ? drafts[selectedRow.rowId] : undefined
  const selectedRegion = selectedRow?.product.journeyPlaces.find(
    (journeyPlace) => journeyPlace.id === selectedDraft?.regionOfStayId
  )

  const onNext = () => {
    if (!selectedRow || !selectedDraft) return
    if (!selectedDraft.departureDate) {
      toast.error('Veuillez sélectionner une date de départ')
      return
    }
    if (!selectedDraft.returnDate) {
      toast.error('Veuillez sélectionner une date de retour')
      return
    }
    if (
      !isAfter(
        DateFormatter.toDate(selectedDraft.returnDate),
        DateFormatter.toDate(selectedDraft.departureDate)
      )
    ) {
      toast.error('La date de retour doit être après la date de départ')
      return
    }
    if (!selectedDraft.numberOfHours) {
      toast.error("Veuillez sélectionner le nombre d'heures de cours")
      return
    }
    if (!selectedRegion) {
      toast.error('Veuillez sélectionner une région de séjour')
      return
    }
    if (!selectedDraft.arrivalPlaceId) {
      toast.error("Veuillez sélectionner un lieu d'arrivée")
      return
    }
    setActiveIndex(1)
  }

  const onSubmit = () => {
    if (!selectedRow || !selectedDraft || !selectedRegion) return
    if (!details.transferMethod) {
      toast.error("Veuillez sélectionner un mode d'arrivée et de départ")
      return
    }
    const arrivalPlace = selectedRegion.place.arrivalPoint.find(
      (place) => place.id === selectedDraft.arrivalPlaceId
    )
    if (!arrivalPlace) {
      toast.error("Veuillez sélectionner un lieu d'arrivée")
      return
    }

    handleSubmit({
      product: selectedRow.product,
      numberOfWeeks: selectedRow.numberOfWeeks,
      departureDate: selectedDraft.departureDate!,
      returnDate: selectedDraft.returnDate,
      numberOfHours: selectedDraft.numberOfHours!,
      regionOfStay: selectedRegion,
      arrivalPlace,
      withOption: details.withOption,
      optionPrice: details.withOption ? selectedRegion.optionPrice : undefined,
      transferMethod: details.transferMethod,
      transferPrice:
        details.transferMethod === OtoIndividualTransferMethodEnum.AIRPORT_PICKUP
          ? selectedRegion.place.price
          : undefined,
      pricing: {
        includeApplicationFees: defaultValues?.pricing?.includeApplicationFees ?? true,
        includeFamilyDiscount: defaultValues?.pricing?.includeFamilyDiscount ?? false
      },
      promoCode: defaultValues?.promoCode
    })
  }

  const iconColorMap = (index: number) =>
    activeIndex === index ? tailwindColors.primary.DEFAULT : tailwindColors.grey.light.active

  return (
    <Modal
      isOpen={isOpen}
      title={'Sélectionner un séjour 1 Professeur pour 1 Elève'}
      icon={LocationFourIcon}
      size={'large'}
      hideActions
      disableOutSideClick
      onCancel={onClose}
    >
      {isOpen && (
        <LineTab
          tabs={[
            {
              label: 'Sélectionner un séjour',
              icon: <TravelTwoIcon color={iconColorMap(0)} />,
              component: (
                <div className={cn('mt-6', { hidden: activeIndex !== 0 })}>
                  <OtoIndividualProductListSelection
                    selectedRowId={selectedRow?.rowId}
                    drafts={drafts}
                    onDraftChange={(rowId, draft) =>
                      setDrafts((prev) => ({ ...prev, [rowId]: draft }))
                    }
                    onSelectRow={setSelectedRow}
                    onSubmit={onNext}
                  />
                </div>
              ),
              disabled: true
            },
            {
              label: 'Details',
              icon: <OptionIcon color={iconColorMap(1)} />,
              component: selectedRegion && (
                <OtoIndividualPropositionDetails
                  regionOfStay={selectedRegion}
                  values={details}
                  childBirthDate={childBirthDate}
                  departureDate={selectedDraft?.departureDate}
                  onChange={setDetails}
                  onCancel={() => setActiveIndex(0)}
                  onSubmit={onSubmit}
                />
              ),
              disabled: true
            }
          ]}
          activeTab={activeIndex}
          wrapperClassName="px-4"
          onTabChange={setActiveIndex}
        />
      )}
    </Modal>
  )
}

export default OtoIndividualPropositionFormModal
